/**
 * Gift Box Upsell - Web Component
 *
 * Renders an "Add to bag" button in the cart drawer for the configured gift box
 * product. The upsell is removed from the markup entirely once the gift box is in
 * the bag (handled in Liquid), so this component only ever needs to add.
 *
 * Integrates with the theme's existing cart event bus:
 *   cart:prepare-bundled-sections → tells the CartDrawer to register its section ID
 *   cart:change                   → triggers CartDrawer re-render
 *   theme:loading:start/end       → shows/hides the global loading indicator
 *   toast:show                    → surfaces error messages to the customer
 */
class GiftBoxUpsell extends HTMLElement {
  connectedCallback() {
    this._button = this.querySelector('.button');
    if (!this._button) return;

    this._variantId = parseInt(this.dataset.variantId, 10);
    this._inFlight = false;
    this._handleClick = this._onClick.bind(this);
    this._button.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this._button?.removeEventListener('click', this._handleClick);
  }

  async _onClick() {
    if (this._inFlight) return;
    this._inFlight = true;
    this._setLoading(true);

    try {
      await this._addToCart();
    } catch (err) {
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: err.message || 'Something went wrong', tone: 'error' },
        })
      );
    } finally {
      this._setLoading(false);
      this._inFlight = false;
    }
  }

  async _addToCart() {
    const sections = this._prepareSections();
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsStr = [...sections].join(',');
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: this._variantId, quantity: 1 }],
        ...(sectionsStr && { sections: sectionsStr }),
      }),
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (!response.ok) {
      throw new Error(await this._parseErrorMessage(response, 'Could not add gift box'));
    }

    const addData = await response.json();
    const sectionId = [...sections][0];
    const renderedHtml = addData.sections?.[sectionId];
    const itemCount = renderedHtml ? this._parseItemCount(renderedHtml) : 1;

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: { item_count: itemCount, sections: addData.sections },
        },
      })
    );
  }

  _parseItemCount(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const heading = doc.querySelector('[slot="header"] .h7');
    const match = heading?.textContent.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  _prepareSections() {
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  async _parseErrorMessage(response, fallback) {
    try {
      const data = await response.json();
      return data.description || data.message || fallback;
    } catch {
      return fallback;
    }
  }

  _setLoading(loading) {
    if (!this._button) return;
    if (loading) {
      this._button.setAttribute('aria-busy', 'true');
    } else {
      this._button.removeAttribute('aria-busy');
    }
    this._button.disabled = loading;
  }
}

if (!window.customElements.get('gift-box-upsell')) {
  window.customElements.define('gift-box-upsell', GiftBoxUpsell);
}
